import { useState } from 'react';
import { AlertTriangle, PauseCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/Button';
import { Field, fieldInputClass } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { useCancel, usePause } from '@/hooks/use-campaign-flow';
import { describeError } from '@/lib/describe-error';

/**
 * Pausing or cancelling a send in flight (spec § 4.1).
 *
 * ── THE OUTCOME IS A NUMBER, AND IT IS THE POINT ──────────────────────────────────
 *
 * Both operations return how many recipients were genuinely still stoppable and how many are past
 * recall. A chunk already handed to the transport is gone — SendGrid will deliver it — and "paused"
 * without that second number is the answer that gets somebody in trouble, because they will report
 * upwards that they stopped a send they only partly stopped.
 *
 * So the dialog does not close on success. It replaces the form with the counts and makes the reader
 * dismiss them, which is the one interaction pattern that guarantees the number was on screen.
 *
 * ── A REASON IS REQUIRED ──────────────────────────────────────────────────────────
 *
 * The server demands one. Stopping a send mid-flight is the kind of decision somebody asks about
 * later, and "who stopped it and why" is not reconstructible from the absence of data.
 *
 * ── CANCEL CONFIRMS WHAT IS IRREVERSIBLE, BY NAME ─────────────────────────────────
 *
 * `ui-ux.md` § 9: a confirmation names the specific thing and what is irreversible about it. Pause is
 * reversible and reads as routine. Cancel is not, and says so — the campaign cannot resume, and the
 * recipients already mailed stay mailed.
 */
export function StopSendDialog({
  campaignId,
  campaignName,
  mode,
  onClose,
}: {
  campaignId: string;
  campaignName: string;
  mode: 'pause' | 'cancel';
  onClose: () => void;
}) {
  const pause = usePause(campaignId);
  const cancel = useCancel(campaignId);

  const action = mode === 'pause' ? pause : cancel;

  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const error = reason.trim().length === 0 ? 'Say why, so the audit log records it.' : undefined;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (error !== undefined) {
      setTouched(true);
      document.getElementById('stop-reason')?.focus();
      return;
    }

    if (mode === 'pause') pause.mutate(reason.trim());
    else cancel.mutate(reason.trim());
  }

  // Success: show the counts rather than closing. See the header comment.
  const outcome = pause.data ?? cancel.data;

  if (outcome !== undefined) {
    const stopped = 'stoppableRecipients' in outcome ? outcome.stoppableRecipients : outcome.stoppedRecipients;
    const committed = outcome.alreadyCommittedRecipients;

    return (
      <Modal
        title={mode === 'pause' ? 'Send paused' : 'Send cancelled'}
        onClose={onClose}
        footer={
          <Button variant="primary" onClick={onClose}>
            Got it
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="rounded-md bg-success-bg px-3 py-2.5">
            <p className="text-2xl font-semibold tabular-nums text-success-ink">
              {stopped.toLocaleString()}
            </p>
            <p className="text-sm text-success-ink">
              {stopped === 1 ? 'recipient was' : 'recipients were'} stopped.
            </p>
          </div>

          {/* Not a footnote. Somebody who paused a send needs to know this before they tell anyone
              they stopped it. */}
          {committed > 0 && (
            <div className="rounded-md bg-warning-bg px-3 py-2.5">
              <p className="flex items-start gap-2 text-sm text-warning-ink">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>
                  <strong className="font-semibold">
                    {committed.toLocaleString()} {committed === 1 ? 'recipient' : 'recipients'} could
                    not be stopped.
                  </strong>{' '}
                  Those messages were already accepted by the transport and will be delivered. Nothing
                  here can recall them.
                </span>
              </p>
            </div>
          )}

          {committed === 0 && (
            <p className="text-sm text-muted">Nothing had reached the transport yet, so none went out.</p>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={mode === 'pause' ? `Pause ${campaignName}?` : `Cancel ${campaignName}?`}
      description={
        mode === 'pause'
          ? 'Chunks not yet handed to the transport stop. You can resume afterwards.'
          : 'This cannot be undone. The campaign will not resume, and anyone already mailed stays mailed.'
      }
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={action.isPending}>
            Keep sending
          </Button>

          <Button
            type="submit"
            form="stop-send"
            variant={mode === 'pause' ? 'primary' : 'danger'}
            disabled={action.isPending}
          >
            {mode === 'pause' ? (
              <PauseCircle className="size-4" aria-hidden="true" />
            ) : (
              <XCircle className="size-4" aria-hidden="true" />
            )}
            {action.isPending
              ? mode === 'pause'
                ? 'Pausing…'
                : 'Cancelling…'
              : mode === 'pause'
                ? 'Pause the send'
                : 'Cancel the send'}
          </Button>
        </>
      }
    >
      <form id="stop-send" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {action.isError && (
          <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink" role="alert">
            {describeError(action.error).message}
          </p>
        )}

        <Field
          id="stop-reason"
          label="Why?"
          hint="Recorded in the audit log against your name."
          error={touched ? error : undefined}
        >
          <input
            id="stop-reason"
            type="text"
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={touched && error !== undefined}
            aria-describedby={error === undefined ? 'stop-reason-hint' : 'stop-reason-error'}
            placeholder={mode === 'pause' ? 'Wrong segment' : 'Broken link in the header'}
            className={fieldInputClass}
          />
        </Field>
      </form>
    </Modal>
  );
}
